<div
  class="flex flex-col w-full bg-component-inner border border-primary-border rounded-2xl p-3 shadow-default focus-within:border-primary transition-all mx-auto"
>
  @if (pendingAttachments().length > 0 && !isRecording()) {
    <div class="flex flex-wrap gap-2 mb-2 px-2">
      @for (file of pendingAttachments(); track file.id) {
        <div
          class="flex items-center gap-2 bg-alt-background border border-secondary-border rounded-lg px-3 py-1.5 shadow-sm"
        >
          <i class="fas fa-file text-secondary-text text-sm"></i>
          <div class="flex flex-col max-w-[120px]">
            <span class="text-xs font-semibold text-primary-text truncate">{{
              file.name
            }}</span>
            <span class="text-[10px] text-secondary-text">{{
              formatSize(file.size)
            }}</span>
          </div>
          <button
            (click)="removeAttachment(file.id)"
            class="text-secondary-text hover:text-danger focus:outline-none ml-1 p-1"
          >
            <i class="fas fa-times text-xs"></i>
          </button>
        </div>
      }
    </div>
  }

  @if (!isRecording()) {
    <nz-mention
      [nzSuggestions]="mentionSuggestions()"
      [nzValueWith]="mentionValueWith"
      nzPrefix="@"
      (nzOnSearchChange)="onMentionSearchChange($event)"
      class="w-full block custom-nz-mention"
    >
      <textarea
        nz-input
        nzMentionTrigger
        #messageTextarea
        [(ngModel)]="messageText"
        [nzAutosize]="{ minRows: 1, maxRows: 7 }"
        [placeholder]="config.texts.inputPlaceholder"
        (keydown.enter)="handleEnterKey($event)"
        class="w-full !bg-transparent !border-none !shadow-none px-2 py-2 text-primary-text resize-y focus:outline-none custom-scrollbar min-h-[40px] mb-2"
      >
      </textarea>
      <ng-container *nzMentionSuggestion="let item">
        <div class="flex items-center gap-2">
          <i [class]="item.icon + ' text-secondary-text'"></i>
          <span>{{ item.label }}</span>
        </div>
      </ng-container>
    </nz-mention>

    <div
      class="flex items-center justify-between border-t border-primary-border pt-2 mt-1"
    >
      <div class="flex items-center gap-2">
        <nz-upload
          [nzBeforeUpload]="beforeUpload"
          [nzMultiple]="true"
          [nzShowUploadList]="false"
        >
          <button
            nz-tooltip
            [nzTooltipTitle]="config.texts.attachButton"
            class="w-8 h-8 rounded-full flex items-center justify-center text-secondary-text hover:text-primary hover:bg-hover-default transition-colors focus:outline-none"
          >
            <i class="fas fa-plus text-base"></i>
          </button>
        </nz-upload>

        <button
          class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-secondary-text hover:text-primary hover:bg-hover-default transition-colors focus:outline-none text-sm font-medium"
        >
          <i class="fa-regular fa-chart-network text-base"></i>
          <span>{{ config.texts.toolsButton }}</span>
        </button>
      </div>

      <div class="flex items-center gap-3">
        <button
          nz-dropdown
          [nzDropdownMenu]="agentMenu"
          nzTrigger="click"
          class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-secondary-text hover:text-primary hover:bg-hover-default transition-colors focus:outline-none text-sm font-medium"
        >
          <span>{{ selectedAiFunctionName() }}</span>
          <i class="fas fa-chevron-down text-xs mt-0.5"></i>
        </button>

        <nz-dropdown-menu #agentMenu="nzDropdownMenu">
          <ul
            nz-menu
            class="min-w-[150px] !rounded-lg !p-1.5 !border !border-primary-border !shadow-lg"
          >
            @for (agent of availableAiFunctions(); track agent.id) {
              <li
                nz-menu-item
                (click)="selectAiFunction(agent.id || agent.name)"
                (keydown.enter)="selectAiFunction(agent.id || agent.name)"
                (keydown.space)="selectAiFunction(agent.id || agent.name)"
                tabindex="0"
                class="!rounded-md !px-3 !py-2"
              >
                <span
                  class="font-medium text-sm"
                  [ngClass]="
                    agent.id === selectedAiFunctionId() ||
                    agent.name === selectedAiFunctionId()
                      ? 'text-primary'
                      : 'text-primary-text'
                  "
                >
                  {{ agent.title || agent.name }}
                </span>
              </li>
            }
          </ul>
        </nz-dropdown-menu>

        @if (
          messageText().trim().length > 0 || pendingAttachments().length > 0
        ) {
          <button
            (click)="dispatchMessage()"
            nz-tooltip
            [nzTooltipTitle]="config.texts.sendButton"
            class="w-8 h-8 rounded-full flex items-center justify-center bg-primary text-primary-button-text hover:bg-hover-primary transition-transform hover:scale-105 focus:outline-none shadow-sm"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        } @else {
          <button
            (click)="startVoiceRecording()"
            nz-tooltip
            [nzTooltipTitle]="config.texts.recordButton"
            class="w-8 h-8 rounded-full flex items-center justify-center text-secondary-text hover:text-primary hover:bg-hover-default transition-colors focus:outline-none"
          >
            <i class="fas fa-microphone text-base"></i>
          </button>
        }
      </div>
    </div>
  } @else {
    <div
      class="flex-1 flex items-center justify-between bg-danger/10 border border-danger/20 rounded-xl px-4 py-2 h-12 shadow-sm w-full"
    >
      <div class="flex items-center gap-3">
        <div class="w-3 h-3 bg-danger rounded-full animate-ping"></div>
        <span class="text-danger font-semibold text-sm">{{
          config.texts.recordingActive
        }}</span>
        <span class="text-primary-text font-mono text-sm ml-2">{{
          getFormattedDuration()
        }}</span>
      </div>

      <div class="flex items-center gap-2">
        <button
          (click)="cancelVoiceRecording()"
          nz-tooltip
          [nzTooltipTitle]="config.texts.cancelRecordButton"
          class="w-8 h-8 rounded-full flex items-center justify-center bg-component-inner text-secondary-text hover:text-danger hover:bg-alt-background transition-colors focus:outline-none shadow-sm"
        >
          <i class="fas fa-trash-alt"></i>
        </button>
        <button
          (click)="stopAndSendVoiceRecording()"
          nz-tooltip
          [nzTooltipTitle]="config.texts.stopRecordButton"
          class="w-8 h-8 rounded-full flex items-center justify-center bg-danger text-white hover:bg-hover-danger transition-colors focus:outline-none shadow-sm"
        >
          <i class="fas fa-paper-plane"></i>
        </button>
      </div>
    </div>
  }
</div>
